import type { UiTheme } from "../theme";
import type { ChartVariant } from "./chartAnnotations";

// PG-31/PG-33 — pure colour/marker logic split out of `WagmiLineChart.tsx`
// (the 500-line cap). Renderer-agnostic: neither function touches wagmi or
// React, both are provable without mounting a chart. `lineColor` isn't a
// hypothetical split either — it already had a caller outside this chart
// (`TierOneCompareRow.tsx`, PG-26) before this file existed.

export type DotAppearance = { color: string; size: number; borderColor?: string; borderWidth?: number };

/** What `dotAppearance`/`neutralDotAppearance` need to know about a drawn
 *  layer — narrower than `WagmiLineChart`'s own `Layer` (no `id`: a
 *  colour/marker decision never depends on WHICH series it is, only on what
 *  is true about it). */
export type DotLayer = { variant: ChartVariant; current: boolean; stale: boolean };

// Exported for PG-26: the compare caption paints each exercise name in its own
// curve's colour, and a legend that computes its own colours is a legend that
// can lie. One source, so a swatch can never show a colour the line does not.
export function lineColor(theme: UiTheme, variant: ChartVariant) {
  if (variant === "focal") return theme.palette.brand500;
  if (variant === "secondary") return theme.palette.ink3;
  return theme.palette.ink;
}

/** The widest dot drawn: the stale one, below. */
export const LARGEST_DOT_SIZE = 5;

// "Now" is a claim about time, so it is EARNED by being recent — never by
// being last, and never by default (HM-1, 2026-07-15). A stale last point
// stays VISIBLE but goes hollow: it is the newest reading we have, so hiding
// it would be a second lie — it just stops claiming to be the present.
export function dotAppearance(theme: UiTheme, layer: DotLayer): DotAppearance {
  if (layer.variant === "secondary") {
    return { color: theme.palette.ink3, size: 2.5 };
  }
  if (layer.variant === "focal") {
    return { color: theme.palette.brand500, size: 3.5 };
  }
  const isNowDot = layer.current;
  const isStaleDot = layer.stale;
  if (isStaleDot) {
    return {
      color: theme.palette.surface,
      size: LARGEST_DOT_SIZE,
      borderColor: theme.palette.ink,
      borderWidth: 2.5,
    };
  }
  return {
    color: isNowDot ? theme.palette.brand500 : theme.palette.ink,
    size: isNowDot ? 4 : 3,
  };
}

// Every non-accent point (i.e. every point except the series' last one —
// `dotAppearance` above) gets the SAME small neutral marker regardless of
// variant: the accent is the one claim about a point worth spending colour
// on, everything else just marks "a sample existed here" (old SVG renderer
// parity, per-point dots). ⛔ This used to say "the last point of the
// series' LAST SEGMENT", which stopped being a thing at PG-36.
export function neutralDotAppearance(theme: UiTheme): DotAppearance {
  return { color: theme.palette.ink3, size: 2 };
}
